import type { OrderKind } from '@/types/order'

export const orderKinds = [
  'package',
  'hot',
  'superior',
  'district',
  'course',
  'star',
  'gift',
  'send-more',
] as const satisfies readonly OrderKind[]

interface OrderEndpoints {
  list: string
  detail: (id: string) => string
  updateAddress?: string
  updateTrack?: string
  shipping?: string
  export: string
}

export interface OrderTypeConfig {
  title: string
  templateType: number
  routePrefix: string
  editable: boolean
  bulkShipping: boolean
  endpoints: OrderEndpoints
}

export const orderTypeConfigs: Record<OrderKind, OrderTypeConfig> = {
  package: {
    title: '平替区订单',
    templateType: 1,
    routePrefix: '/order',
    editable: true,
    bulkShipping: true,
    endpoints: {
      list: '/business/tbk/order/list',
      detail: (id) => `/business/tbk/order/detail/${id}`,
      updateAddress: '/business/tbk/order/updateOrderAddress',
      updateTrack: '/business/tbk/order/updateOrderTrack',
      shipping: '/business/tbk/order/shipping',
      export: '/business/tbk/order/export',
    },
  },
  hot: {
    title: '限量购订单',
    templateType: 2,
    routePrefix: '/order/hotOrder',
    editable: true,
    bulkShipping: true,
    endpoints: {
      list: '/business/tbk/order/burstList',
      detail: (id) => `/business/tbk/order/burstDetail/${id}`,
      updateAddress: '/business/tbk/order/updateBurstAddress',
      updateTrack: '/business/tbk/order/updateBurstTrack',
      shipping: '/business/tbk/order/burstShipping',
      export: '/business/tbk/order/burstExport',
    },
  },
  superior: {
    title: '自营订单',
    templateType: 3,
    routePrefix: '/order/superiorOrder',
    editable: true,
    bulkShipping: true,
    endpoints: {
      list: '/business/tbk/order/fineList',
      detail: (id) => `/business/tbk/order/fineDetail/${id}`,
      updateAddress: '/business/tbk/order/updateFineAddress',
      updateTrack: '/business/tbk/order/updateFineTrack',
      shipping: '/business/tbk/order/fineShipping',
      export: '/business/tbk/order/fineExport',
    },
  },
  district: {
    title: '区代订单',
    templateType: 3,
    routePrefix: '/order/districtOrder',
    editable: true,
    bulkShipping: true,
    endpoints: {
      list: '/business/tbk/order/regionList',
      // 区代在旧后端中复用自营商品对应的详情和写接口，联调确认前不得推导为 region*。
      detail: (id) => `/business/tbk/order/fineDetail/${id}`,
      updateAddress: '/business/tbk/order/updateFineAddress',
      updateTrack: '/business/tbk/order/updateFineTrack',
      shipping: '/business/tbk/order/fineShipping',
      export: '/business/tbk/order/regionExport',
    },
  },
  course: {
    title: '课程订单',
    templateType: 2,
    routePrefix: '/order/courseOrder',
    // 旧课程订单只有查看和导出接口，禁止从通用页面开放编辑或发货。
    editable: false,
    bulkShipping: false,
    endpoints: {
      list: '/business/tbk/order/courseList',
      detail: (id) => `/business/tbk/order/courseDetail/${id}`,
      export: '/business/tbk/order/courseExport',
    },
  },
  star: {
    title: '9.9元订单',
    templateType: 5,
    routePrefix: '/order/starOrder',
    editable: true,
    bulkShipping: true,
    endpoints: {
      list: '/business/tbk/order/starList',
      detail: (id) => `/business/tbk/order/starDetail/${id}`,
      updateAddress: '/business/tbk/order/updateStarAddress',
      updateTrack: '/business/tbk/order/updateStarTrack',
      shipping: '/business/tbk/order/starShipping',
      export: '/business/tbk/order/starExport',
    },
  },
  gift: {
    title: '零元购订单',
    templateType: 7,
    routePrefix: '/order/giftOrder',
    editable: true,
    bulkShipping: true,
    endpoints: {
      list: '/business/tbk/order/giftList',
      detail: (id) => `/business/tbk/order/giftDetail/${id}`,
      updateAddress: '/business/tbk/order/updateGiftAddress',
      updateTrack: '/business/tbk/order/updateGiftTrack',
      shipping: '/business/tbk/order/giftShipping',
      export: '/business/tbk/order/giftExport',
    },
  },
  'send-more': {
    title: '送多多订单',
    templateType: 6,
    routePrefix: '/order/sendMoreOrder',
    editable: true,
    bulkShipping: true,
    endpoints: {
      // 后端路径首字母为大写，属于兼容协议的一部分。
      list: '/business/tbk/order/SendMoreList',
      detail: (id) => `/business/tbk/order/sendMoreDetail/${id}`,
      updateAddress: '/business/tbk/order/updateSendMoreOrder',
      updateTrack: '/business/tbk/order/updateSendMoreTrack',
      shipping: '/business/tbk/order/sendMoreShipping',
      export: '/business/tbk/order/sendMoreExport',
    },
  },
}

export function getOrderTypeConfig(kind: OrderKind): OrderTypeConfig {
  return orderTypeConfigs[kind]
}
